<!--
  TaskAssigneePicker.vue

  Searchable multi-assignee picker for tasks. v-model is the selected set: an
  array of user record ids (number[]). Toggling a row emits the next array, so
  the v-model is the single source of truth — search can narrow the displayed
  rows freely without affecting which users are selected.

  The dropdown is teleported to <body> and positioned from the trigger's rect so
  it isn't clipped by a modal's overflow. Mirrors the prior-art pattern in
  AutocompleteInput.vue.
-->
<template>
  <div ref="rootEl" class="assignee-picker" :class="{ 'is-open': isOpen }">
    <button
      ref="triggerEl"
      type="button"
      class="btn assignee-picker-trigger"
      :disabled="disabled"
      @click="toggle"
    >
      <span class="flex-1 text-left">{{ currentLabel }}</span>
      <span class="icon is-small"><font-awesome-icon icon="chevron-down" /></span>
    </button>

    <Teleport v-if="isOpen" :to="teleportTarget">
      <div class="assignee-picker-panel" :style="panelStyle">
        <div class="p-2">
          <input
            ref="searchInput"
            v-model="search"
            class="input is-small"
            type="text"
            placeholder="Search users..."
          />
        </div>
        <div class="assignee-picker-list">
          <button
            type="button"
            class="assignee-picker-row"
            :class="{ 'is-selected': selectedIds.length === 0 }"
            @click="clearAll"
          >
            <em>Unassigned</em>
          </button>
          <button
            v-for="user in displayedUsers"
            :key="user.record_id"
            type="button"
            class="assignee-picker-row"
            :class="{ 'is-selected': isSelected(user) }"
            @click="toggleUser(user)"
          >
            {{ user.name }}
            <span v-if="isSelected(user)" class="icon is-small ml-2">
              <font-awesome-icon icon="check" />
            </span>
          </button>
          <p v-if="displayedUsers.length === 0" class="p-2 text-7 text-muted">No matches</p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue"
import type { User } from "@auger/core"

type Assignee = User & { record_id: number }

const props = defineProps({
  modelValue: { type: Array as () => number[], default: () => [] },
  users: { type: Array as () => User[], required: true },
  disabled: { type: Boolean, default: false },
})
const emit = defineEmits<{ "update:modelValue": [value: number[]] }>()

const isOpen = ref(false)
const search = ref("")
const searchInput = ref<HTMLInputElement | null>(null)
const rootEl = ref<HTMLElement>()
const triggerEl = ref<HTMLElement>()

const usersWithRecord = computed(() => props.users.filter((u): u is Assignee => !!u.record_id))

// The selected set IS the v-model — a number[] of user record_ids. There's no
// separate selection state to keep in sync, so search can freely narrow the
// displayed rows without risking already-selected (non-matching) assignees.
const selectedIds = computed(() => props.modelValue ?? [])
const isSelected = (user: Assignee) => selectedIds.value.includes(user.record_id)

const displayedUsers = computed(() => {
  const needle = search.value.trim().toLowerCase()
  if (!needle) return usersWithRecord.value
  return usersWithRecord.value.filter((u) => u.name?.toLowerCase().includes(needle))
})

// --- Teleported dropdown positioning (escape modal overflow clipping) ---
// Teleport to <body> with fixed positioning so the dropdown isn't clipped by a
// modal's (or any ancestor's) overflow. The scroll/resize listeners recompute
// from the trigger's live viewport rect, so the panel tracks the trigger even
// while a modal scrolls. (Anchoring inside .modal-content was unreliable — it
// isn't position:relative, so absolute coords resolved against the wrong
// offset parent.)
const panelPos = ref({ top: 0, left: 0, width: 0 })
const teleportTarget = "body"
const panelStyle = computed(() => ({
  position: "fixed" as const,
  top: `${panelPos.value.top}px`,
  left: `${panelPos.value.left}px`,
  width: `${panelPos.value.width}px`,
}))

function updatePanelPosition() {
  const trigger = triggerEl.value
  if (!trigger) return
  const rect = trigger.getBoundingClientRect()
  panelPos.value = { top: rect.bottom + 4, left: rect.left, width: rect.width }
}

const reposition = () => updatePanelPosition()

function addScrollListeners() {
  let el: HTMLElement | null = triggerEl.value || null
  while (el) {
    el.addEventListener("scroll", reposition, { passive: true })
    el = el.parentElement
  }
  window.addEventListener("scroll", reposition, { passive: true })
  window.addEventListener("resize", reposition, { passive: true })
}

function removeScrollListeners() {
  let el: HTMLElement | null = triggerEl.value || null
  while (el) {
    el.removeEventListener("scroll", reposition)
    el = el.parentElement
  }
  window.removeEventListener("scroll", reposition)
  window.removeEventListener("resize", reposition)
}

onBeforeUnmount(removeScrollListeners)

watch(isOpen, (open) => {
  if (open) {
    nextTick(() => {
      updatePanelPosition()
      addScrollListeners()
      searchInput.value?.focus()
    })
  } else {
    removeScrollListeners()
  }
})

const currentLabel = computed(() => {
  const ids = selectedIds.value
  if (ids.length === 0) return "Unassigned"
  if (ids.length === 1) {
    const user = usersWithRecord.value.find((u) => u.record_id === ids[0])
    return user?.name ?? "1 assignee"
  }
  return `${ids.length} assignees`
})

function toggle() {
  isOpen.value = !isOpen.value
}

// Multi-select: toggling a row keeps the panel open so several can be picked.
function toggleUser(user: Assignee) {
  const id = user.record_id
  const next = isSelected(user)
    ? selectedIds.value.filter((existing) => existing !== id)
    : [...selectedIds.value, id]
  emit("update:modelValue", next)
}

function clearAll() {
  emit("update:modelValue", [])
  isOpen.value = false
}
</script>

<style scoped>
.assignee-picker {
  position: relative;
  width: 100%;
}
.assignee-picker-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
/* Teleported to <body>; position/top/left/width are set inline via panelStyle
   so the dropdown isn't clipped by modal overflow. */
.assignee-picker-panel {
  z-index: 10000;
  background: white;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  box-shadow: var(--auger-shadow-3);
  max-height: 320px;
  display: flex;
  flex-direction: column;
}
.assignee-picker-list {
  overflow-y: auto;
  flex: 1;
}
.assignee-picker-row {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.4rem 0.75rem;
  background: white;
  border: 0;
  text-align: left;
  cursor: pointer;
  font-size: 0.9rem;
}
.assignee-picker-row:hover {
  background: var(--auger-bg-muted);
}
.assignee-picker-row.is-selected {
  background: #e8f0fe;
  font-weight: 600;
}
</style>
